'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface AssessmentQuestion {
  id: string;
  question: string;
  options: string[];
}

interface CapabilityAssessmentProps {
  questions: AssessmentQuestion[];
}

export default function CapabilityAssessment({ questions }: CapabilityAssessmentProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (questionId: string, answer: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };

  const progress = ((currentStep + 1) / questions.length) * 100;
  const currentQuestion = questions[currentStep];
  const isAnswered = answers[currentQuestion?.id];

  if (showResults) {
    return (
      <div className="bg-gradient-to-br from-brand-conversion/10 to-brand-primary/10 rounded-xl p-8 border-2 border-brand-conversion">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-brand-conversion rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckBadgeIcon" size={32} variant="solid" className="text-white" />
          </div>
          <h3 className="text-2xl font-headline font-bold text-primary mb-2">
            Assessment Complete!
          </h3>
          <p className="text-sm font-body text-muted-foreground">
            Based on your responses, we recommend the following services
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <div className="bg-white rounded-lg p-4 border border-border">
            <div className="flex items-center space-x-3">
              <Icon name="ShieldCheckIcon" size={24} className="text-brand-primary" />
              <div>
                <h4 className="text-sm font-headline font-semibold text-primary">Managed IT Services</h4>
                <p className="text-xs font-body text-muted-foreground">Comprehensive infrastructure management</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-border">
            <div className="flex items-center space-x-3">
              <Icon name="CloudIcon" size={24} className="text-brand-secondary" />
              <div>
                <h4 className="text-sm font-headline font-semibold text-primary">Cloud Solutions</h4>
                <p className="text-xs font-body text-muted-foreground">Scalable cloud infrastructure</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleReset}
            className="flex-1 px-6 py-3 border-2 border-border hover:border-brand-primary text-primary hover:text-brand-primary rounded-lg font-cta font-semibold text-sm transition-all duration-200"
          >
            Retake Assessment
          </button>
          <button className="flex-1 px-6 py-3 bg-brand-conversion hover:bg-brand-conversion/90 text-white rounded-lg font-cta font-semibold text-sm transition-all duration-200 transform hover:scale-105">
            Schedule Consultation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-6">
        <h3 className="text-xl font-headline font-bold text-white mb-1">
          Interactive Capability Assessment
        </h3>
        <p className="text-sm font-body text-white/80">
          Answer a few questions to find the right services for your business
        </p>
      </div>

      {/* Progress Bar */}
      <div className="px-6 pt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-cta font-semibold text-muted-foreground">
            Question {currentStep + 1} of {questions.length}
          </span>
          <span className="text-xs font-cta font-semibold text-brand-primary">
            {Math.round(progress)}% Complete
          </span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-brand-primary to-brand-conversion transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="p-6">
        <h4 className="text-lg font-headline font-semibold text-primary mb-4">
          {currentQuestion?.question}
        </h4>

        <div className="space-y-3 mb-6">
          {currentQuestion?.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswer(currentQuestion.id, option)}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                answers[currentQuestion.id] === option
                  ? 'border-brand-primary bg-brand-primary/5' :'border-border hover:border-brand-primary/50'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  answers[currentQuestion.id] === option
                    ? 'border-brand-primary bg-brand-primary' :'border-border'
                }`}>
                  {answers[currentQuestion.id] === option && (
                    <Icon name="CheckIcon" size={12} className="text-white" />
                  )}
                </div>
                <span className="text-sm font-body text-foreground">{option}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center space-x-3">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-6 py-3 border-2 border-border hover:border-brand-primary text-primary hover:text-brand-primary rounded-lg font-cta font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={!isAnswered}
            className="flex-1 px-6 py-3 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-cta font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
          >
            {currentStep === questions.length - 1 ? 'View Results' : 'Next Question'}
          </button>
        </div>
      </div>
    </div>
  );
}